/**
 * PageSkeletons
 * Page-specific loading skeletons that EXACTLY match the internal loading states
 * CRITICAL: These must be identical to internal page skeletons to prevent layout shift
 * 
 * The strategy is simple: use the same skeleton components that pages use internally
 * when their loading state is true. This ensures seamless Suspense → Page → Data transitions.
 */

import React from 'react';
import { Box, Grid } from '@mui/material';
import { 
  DashboardSkeleton,
  KPICardSkeleton, 
  ChartSkeleton, 
  TableSkeleton, 
  StatsCardSkeleton,
  IncidentCardSkeleton,
  PageHeaderSkeleton as LoadingPageHeaderSkeleton,
} from './LoadingSkeleton';
import { PageHeader } from './PageHeader';
import { tokens } from '../design-system';

/**
 * PageHeaderSkeleton
 * Re-export from LoadingSkeleton for consistency
 */
export const PageHeaderSkeleton = LoadingPageHeaderSkeleton;

/**
 * DashboardPageSkeleton
 * Uses the EXACT same DashboardSkeleton component that Dashboard.tsx uses internally
 */
export const DashboardPageSkeleton: React.FC = () => <DashboardSkeleton />;

/**
 * AnalyticsPageSkeleton  
 * EXACTLY matches Analytics.tsx internal loading skeleton (lines 143-170)
 * Uses real PageHeader component instead of skeleton to match internal behavior
 */
export const AnalyticsPageSkeleton: React.FC = () => (
  <Box sx={{ maxWidth: 1400, margin: '0 auto' }}>
    <PageHeader title="Analytics" subtitle="Service metrics, trends, and performance analysis" />
    
    {/* KPI Cards Skeleton - matches Analytics loading exactly */}
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <KPICardSkeleton />
        </Grid>
      ))}
    </Grid>
    
    {/* Charts Grid - matches Analytics loading exactly: 8+4 split, height 400 */}
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} md={8}>
        <ChartSkeleton height={400} />
      </Grid>
      <Grid item xs={12} md={4}>
        <ChartSkeleton height={400} />
      </Grid>
    </Grid>
    
    {/* Table */}
    <TableSkeleton />
  </Box>
);

/**
 * IncidentsPageSkeleton
 * EXACTLY matches Incidents.tsx internal loading skeleton (lines 143-168)
 * Uses real PageHeader component instead of skeleton to match internal behavior
 */
export const IncidentsPageSkeleton: React.FC = () => (
  <Box sx={{ maxWidth: 1400, margin: '0 auto' }}>
    <PageHeader 
      title="Incidents" 
      subtitle="Track and manage service interruptions" 
    />
    
    {/* Filter Bar Skeleton - matches internal skeleton exactly */}
    <Box sx={{ mb: 4, height: 60, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', borderRadius: tokens.radius.lg }} />
    
    {/* Stats Cards Skeleton */}
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <StatsCardSkeleton />
        </Grid>
      ))}
    </Grid>
    
    {/* Incident Cards Skeleton */}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {Array.from({ length: 3 }).map((_, i) => (
        <IncidentCardSkeleton key={i} />
      ))}
    </Box>
  </Box>
);

export default {
  DashboardPageSkeleton,
  AnalyticsPageSkeleton,
  IncidentsPageSkeleton,
  PageHeaderSkeleton,
};
